import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { HelpLabel } from '@/components/common/help/HelpLabel'
import { helpKey } from '@/lib/action-modules/help'
import { isScoreRangeRewardCalculationTypeModel, type RewardCalculationTypeEditorProps, type ScoreRange, type ScoreRangeRewardCalculationTypeModel } from '@/lib/action-modules/registry/reward/types'

export default function ScoreRangeRewardEditor(props: RewardCalculationTypeEditorProps) {
  const { t } = useTranslation('action-modules-wizard')
  const value = props.value
  const model = isScoreRangeRewardCalculationTypeModel(value) ? value : null
  const scoreRanges = model?.scoreRanges ?? []

  // Editing a bound re-sorts the ranges and chains each `scoreFrom` to the previous `scoreTo`,
  // so the ranges stay contiguous (behaviour ported from the old portal).
  const onChangeRange = useCallback(
    (newRange: ScoreRange, originalRange: ScoreRange) => {
      const updated = scoreRanges.map(sr => (sr === originalRange ? newRange : sr))
      updated.sort((a, b) => a.scoreTo - b.scoreTo)
      for (let i = 1; i < updated.length; i++) {
        updated[i] = { ...updated[i], scoreFrom: updated[i - 1].scoreTo }
      }
      props.onChanged({ ...model, scoreRanges: updated } as ScoreRangeRewardCalculationTypeModel)
    },
    [scoreRanges, model, props],
  )

  const onAdd = useCallback(() => {
    props.onChanged({
      ...model,
      scoreRanges: [...scoreRanges, { scoreFrom: 0, scoreTo: 0, pointsRewarded: 0 }],
    } as ScoreRangeRewardCalculationTypeModel)
  }, [scoreRanges, model, props])

  const onDelete = useCallback(
    (deleted: ScoreRange) => {
      props.onChanged({
        ...model,
        scoreRanges: scoreRanges.filter(sr => sr !== deleted),
      } as ScoreRangeRewardCalculationTypeModel)
    },
    [scoreRanges, model, props],
  )

  if (!model) return <>{t('editor.invalidValue')}</>

  return (
    <div className="flex flex-col gap-3">
      <HelpLabel helpKey={helpKey('rewardScoreRanges')}>{t('rewardCalculationType.scoreRange.scoreRanges')}</HelpLabel>
      <div className="flex flex-col gap-2">
        {scoreRanges.map((sr, i) => (
          <ScoreRangeRow
            key={`${sr.scoreFrom}_${sr.scoreTo}_${sr.pointsRewarded}_${i}`}
            range={sr}
            isFirst={i === 0}
            onChange={r => onChangeRange(r, sr)}
            onDelete={() => onDelete(sr)}
          />
        ))}
        <Button type="button" variant="outline" size="sm" className="self-start" onClick={onAdd}>
          <Plus className="size-4" /> {t('rewardCalculationType.scoreRange.add')}
        </Button>
      </div>
    </div>
  )
}

function ScoreRangeRow(props: {
  range: ScoreRange
  isFirst: boolean
  onChange: (range: ScoreRange) => void
  onDelete: () => void
}) {
  const { t } = useTranslation('action-modules-wizard')
  const key = `${props.range.scoreFrom}_${props.range.scoreTo}_${props.range.pointsRewarded}`
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        {props.isFirst ? (
          <Input
            key={`${key}_from`}
            type="number"
            aria-label={t('rewardCalculationType.scoreRange.from')}
            defaultValue={props.range.scoreFrom}
            onBlur={e => props.onChange({ ...props.range, scoreFrom: Number(e.target.value) })}
          />
        ) : (
          <div className="flex h-9 items-center px-1 text-sm text-muted-foreground">{props.range.scoreFrom}</div>
        )}
      </div>
      <span className="pb-2 text-muted-foreground">&lt;</span>
      <div className="flex-1">
        <Input
          key={`${key}_to`}
          type="number"
          aria-label={t('rewardCalculationType.scoreRange.to')}
          defaultValue={props.range.scoreTo}
          onBlur={e => props.onChange({ ...props.range, scoreTo: Number(e.target.value) })}
        />
      </div>
      <ArrowRight className="mb-2 size-4 shrink-0 text-muted-foreground" />
      <div className="flex-1">
        <Input
          key={`${key}_points`}
          type="number"
          aria-label={t('rewardCalculationType.scoreRange.reward')}
          defaultValue={props.range.pointsRewarded}
          onBlur={e => props.onChange({ ...props.range, pointsRewarded: Number(e.target.value) })}
        />
      </div>
      <Button type="button" variant="ghost" size="icon" onClick={props.onDelete}>
        <Trash2 className="size-4" />
      </Button>
    </div>
  )
}
